import React from 'react';
import StopCard from '../components/StopCard';
import SystemShell, { useGoHome } from '../components/SystemShell';
import { trace } from '../lib/trace';

/*
  The system states that keep the shell, from Group H.

  **Built 10 September 2026 for job 25.** The record is `docs/design/group-h/`
  and the wording below is that group's, word for word: it was approved on 5
  September 2026 and is not paraphrased here.

  Both of these had NO screen in OnHand before today, and each one failed
  silently in its own way, which is this project's oldest lesson wearing two
  more coats.
*/

/*
  **Page not found. It did not exist.** `App.jsx` sent every address it did not
  recognise straight to the home page, with nothing said, so a mistyped or stale
  link looked exactly like OnHand ignoring the person who followed it.

  **It keeps the shell**, rule 35: you are signed in, the app is fine, only this
  address is not, and the menu is the way out.
*/
export function NotFound() {
  const goHome = useGoHome();
  /* What the person actually met, and where they were sent from. Nothing has
     ever recorded a wrong address in OnHand, so nobody could have told whether
     this happens once a year or twenty times a day. */
  React.useEffect(() => {
    trace('not-found', { path: window.location.pathname });
  }, []);
  return (
    <SystemShell band="Not found">
      <StopCard
        mark="lost"
        tone="blue"
        title="That page is not here"
        says="The address might be mistyped, or the page may have been renamed. Nothing has gone wrong with your work."
        act={{ label: 'Go to Home', onClick: goHome }}
      />
    </SystemShell>
  );
}

/*
  **Not yours to open. It did not exist either**: no screen, no message, no
  wording anywhere in OnHand.

  **Nobody has hit it yet and that is not a reason to leave it out.** Everybody
  who can sign in today is the owner. The moment "Expand the user roles" runs,
  somebody reaches a page that is not theirs, and what they would get is
  nothing at all.

  **It says what to do rather than what is wrong.** Not "403", not "forbidden",
  and not the name of the rule that refused: those are facts about OnHand's
  plumbing and none of them is something the person can act on. What they can
  act on is that an administrator can change what they reach.
*/
export function NoPermission({ what }) {
  const goHome = useGoHome();
  React.useEffect(() => {
    trace('no-permission', { what: what || window.location.pathname });
  }, [what]);
  return (
    <SystemShell band={what || 'Not yours'}>
      <StopCard
        mark="shut"
        tone="grey"
        title="This is not yours to open"
        says="Your job level does not include this page. If you need it, an administrator can change what you can reach."
        act={{ label: 'Go to Home', onClick: goHome }}
      />
    </SystemShell>
  );
}
